import React, { useCallback, useEffect, useRef, useState } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import InstagramIcon from "@mui/icons-material/Instagram";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import business from "../data/business.json";
import instagramPhotos from "../data/instagram";

const TILE = 280;
const TILE_MOBILE = 220;
const GAP = 16;

const styles = theme => ({
  section: {
    backgroundColor: "#212529",
    color: "#fff",
    padding: "6rem 0",
    [theme.breakpoints.down("sm")]: {
      padding: "4rem 0"
    }
  },
  inner: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    [theme.breakpoints.down("xs")]: {
      padding: "0 16px"
    }
  },
  heading: {
    marginBottom: "0.75rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "2rem"
    }
  },
  handle: {
    color: "#EC9524",
    fontWeight: 700,
    fontSize: "1.1rem",
    "&:hover, &:focus": {
      color: "#f5b25c"
    }
  },
  carousel: {
    position: "relative",
    marginTop: "2.5rem"
  },
  track: {
    display: "flex",
    gap: GAP,
    width: "max-content",
    maxWidth: "100%",
    margin: "0 auto",
    padding: "4px 0",
    listStyle: "none",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  },
  tile: {
    flex: "0 0 auto",
    width: TILE,
    height: TILE,
    scrollSnapAlign: "start",
    [theme.breakpoints.down("xs")]: {
      width: TILE_MOBILE,
      height: TILE_MOBILE
    }
  },
  tileLink: {
    position: "relative",
    display: "block",
    width: "100%",
    height: "100%",
    borderRadius: 12,
    overflow: "hidden",
    "&:focus-visible": {
      outline: "3px solid #EC9524",
      outlineOffset: 2
    },
    "&:hover $tileImage, &:focus $tileImage": {
      transform: "scale(1.05)"
    },
    "&:hover $tileOverlay, &:focus $tileOverlay": {
      opacity: 1
    }
  },
  tileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s",
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none"
    }
  },
  tileOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    color: "#fff",
    opacity: 0,
    transition: "opacity 0.2s"
  },
  credit: {
    position: "absolute",
    left: 8,
    bottom: 8,
    maxWidth: "calc(100% - 16px)",
    padding: "2px 8px",
    borderRadius: 4,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    color: "#fff",
    fontSize: "0.75rem",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    border: 0,
    borderRadius: "50%",
    backgroundColor: "#fff",
    color: "#212529",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#EC9524"
    },
    "&:focus-visible": {
      outline: "3px solid #EC9524",
      outlineOffset: 2
    },
    [theme.breakpoints.down("xs")]: {
      display: "none"
    }
  },
  prev: {
    left: -8
  },
  next: {
    right: -8
  },
  follow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 52,
    marginTop: "2.5rem",
    padding: "10px 28px",
    border: "3px solid #fff",
    borderRadius: "3em",
    color: "#fff",
    fontWeight: 700,
    textDecoration: "none",
    "&:hover, &:focus": {
      backgroundColor: "#fff",
      color: "#212529",
      textDecoration: "none"
    }
  }
});

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const InstagramGallery = ({ classes }) => {
  const instagram = business.social.find(s => s.network === "Instagram");
  const trackRef = useRef(null);
  const [scroll, setScroll] = useState({ canPrev: false, canNext: false });

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setScroll({
      canPrev: track.scrollLeft > 4,
      canNext: track.scrollLeft + track.clientWidth < track.scrollWidth - 4
    });
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows]);

  const scrollBy = direction => {
    const track = trackRef.current;
    track.scrollBy({
      left: direction * Math.max(track.clientWidth * 0.8, TILE),
      behavior: prefersReducedMotion() ? "auto" : "smooth"
    });
  };

  if (!instagram || instagramPhotos.length === 0) return null;

  return (
    <section id="instagram" className={classes.section} aria-labelledby="instagram-heading">
      <div className={classes.inner}>
        <h2 id="instagram-heading" className={`h1 ${classes.heading}`}>Follow Along on Instagram</h2>
        <a href={instagram.url} target="_blank" rel="noopener noreferrer" className={classes.handle}>
          {instagram.handle}<span className="sr-only"> on Instagram (opens in a new tab)</span>
        </a>

        <div className={classes.carousel}>
          {scroll.canPrev && (
            <button type="button" className={`${classes.arrow} ${classes.prev}`} onClick={() => scrollBy(-1)} aria-label="Previous photos">
              <ChevronLeftIcon />
            </button>
          )}
          <ul ref={trackRef} className={classes.track} onScroll={updateArrows} aria-label={`Photos from ${instagram.handle}`}>
            {instagramPhotos.map(photo => (
              <li key={photo.image} className={classes.tile}>
                <a href={photo.url || instagram.url} target="_blank" rel="noopener noreferrer" className={classes.tileLink}>
                  <img src={photo.image} alt={photo.alt} width="640" height="640" loading="lazy" className={classes.tileImage} />
                  <span className={classes.tileOverlay} aria-hidden="true"><InstagramIcon fontSize="large" /></span>
                  {photo.credit && <span className={classes.credit}>{photo.credit}</span>}
                  <span className="sr-only"> (opens Instagram in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
          {scroll.canNext && (
            <button type="button" className={`${classes.arrow} ${classes.next}`} onClick={() => scrollBy(1)} aria-label="Next photos">
              <ChevronRightIcon />
            </button>
          )}
        </div>

        <a href={instagram.url} target="_blank" rel="noopener noreferrer" className={classes.follow}>
          <InstagramIcon aria-hidden="true" /> Follow {instagram.handle}
          <span className="sr-only"> on Instagram (opens in a new tab)</span>
        </a>
      </div>
    </section>
  );
};

export default withStyles(styles)(InstagramGallery);
